import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AutoCompleteData,
  IAutoCompleteData,
} from '../../interfaces/i-auto-complete-data';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import {
  ISelectListItem,
  SelectListItem,
} from '../../interfaces/i-select-list-item';
import { SearchTagResponse } from 'src/app/core/models/i-search-tag-response';
import { TagService } from 'src/app/content-management/shared/services/tag.service';
import { TagType } from 'src/app/content-management/shared/enums/tag-type';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ICreateTagResponse } from 'src/app/core/models/i-create-tag-response';
import {
  ITypedSelectListItem,
  TypedSelectListItem,
} from '../../interfaces/i-typed-select-list-item';
import { SurtitleTagType } from 'src/app/content-management/shared/enums/surtitle-tag-type';
import { ISurtitle, Surtitle } from 'src/app/core/models/i-surtitle';
import { SurtitleTypeString } from 'src/app/content-management/shared/enums/surtitle-type-string';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';
import { ISectorLanguageResourceDto } from 'src/app/core/dtos/interfaces/i-sector-language-resource-dto';
import { ExternalDataService } from 'src/app/shared/services/external-data.service';

@Component({
  selector: 'app-surtitles',
  templateUrl: './surtitles.component.html',
  styleUrls: ['./surtitles.component.scss'],
})
export class SurtitlesComponent implements OnInit, OnDestroy {
  @Input() isExpandedByDefault = true;
  @Input() isExpandable = true;
  @Input() surtitleControl: FormArray<FormControl<ISurtitle>>;
  @Input() isShowingSurtitles = true;
  @Input() tagControl: FormArray<FormControl<ITag>>;
  @Input() typedTags: FormArray<FormControl<ITypedSelectListItem>>;
  @Input() currentEditionLanguage: FormControl<string>;
  @Input() detailedTopicList: FormArray<FormControl<ITopicWithTranslations>>;

  isSearchingSurtitles: boolean = false;
  editMode: boolean = false;

  SurtitleTagType = SurtitleTagType;
  SurtitleTypeString = SurtitleTypeString;

  searchSoftTagFormControl: FormControl = new FormControl('');
  softTagSearchResults: IAutoCompleteData[];

  selectedSurtitleList: ISelectListItem[] = [];
  surtitleDisplayNameInput: FormControl = new FormControl('');

  existingTopicTags: ISelectListItem[] = [];
  existingTags: ISelectListItem[] = [];
  existingCompanyTags: ISelectListItem[] = [];
  existingManagerTags: ISelectListItem[] = [];
  existingFundTags: ISelectListItem[] = [];
  existingSectorTags: ISelectListItem[] = [];
  existingAssetClassTags: ISelectListItem[] = [];
  existingInvestmentTrustTags: ISelectListItem[] = [];
  existingShareTags: ISelectListItem[] = [];

  destroy$ = new Subject<boolean>();
  typedTagsTranslations: ISectorLanguageResourceDto;

  tagGroups = [
    { label: 'Topics', list: this.existingTopicTags },
    { label: 'Tags', list: this.existingTags },
    { label: 'Companies', list: this.existingCompanyTags },
    { label: 'Managers', list: this.existingManagerTags },
    { label: 'Funds', list: this.existingFundTags },
    { label: 'Sectors', list: this.existingSectorTags },
    { label: 'Asset Classes', list: this.existingAssetClassTags },
    { label: 'Investment Trusts', list: this.existingInvestmentTrustTags },
    { label: 'Shares', list: this.existingShareTags },
  ];

  constructor(
    public taggingService: TagService,
    public dialogService: DialogService,
    public topicService: TopicService,
    public formBuilder: FormBuilder,
    public externalService: ExternalDataService
  ) {
    this.typedTags = this.formBuilder.array<FormControl<ITypedSelectListItem>>(
      []
    );
  }

  ngOnInit() {
    this.surtitleDisplayNameInput.setValue(
      this.surtitleControl.value[0]?.displayText?.trim()
        ? this.surtitleControl.value[0].displayText
        : this.surtitleControl.value[0]?.name || ''
    );

    this.surtitleDisplayNameInput.valueChanges.subscribe({
      next: (x: string) => {
        if (!x.trim()) {
          this.applyDisplayText('');
        } else {
          this.applyDisplayText(x);
        }
      },
    });

    this.selectedSurtitleList = this.surtitleControl.value.map(
      (surtitle: ISurtitle) =>
        new SelectListItem(surtitle.id, surtitle.displayText)
    );

    this.surtitleControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (surtitles: ISurtitle[]) => {
        if (surtitles.length > 0) {
          let isNewSurtitle =
            this.selectedSurtitleList.length === 0 ||
            this.selectedSurtitleList[0].id !== surtitles[0].id;

          this.selectedSurtitleList = surtitles.map(
            (s) => new SelectListItem(s.id, s.displayText)
          );

          if (isNewSurtitle) {
            this.checkIfTranslationAvailable();
          }
        }
      },
    });

    this.searchSoftTagFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$),
        tap(() => {
          this.isSearchingSurtitles = true;
        }),
        switchMap((searchedValue: string) => {
          if (searchedValue === '') {
            this.softTagSearchResults = [];
          }
          const getDataObs$ = searchedValue
            ? this.taggingService.get$(searchedValue, TagType.Soft)
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingSurtitles = false;
            })
          );
        })
      )
      .subscribe({
        next: (x: SearchTagResponse) => {
          if (x && x.results) {
            this.softTagSearchResults = x.results.map((p) => {
              return { displayName: p.text, id: p.id };
            });
          } else {
            this.softTagSearchResults = [];
          }
        },
      });

    this.currentEditionLanguage.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap((language) =>
          this.externalService.getTranslationsByLocale$(language)
        )
      )
      .subscribe({
        next: (tagTranslations) => {
          this.typedTagsTranslations = tagTranslations;
          this.checkIfTranslationAvailable();
        },
      });

    this.externalService
      .getTranslationsByLocale$(this.currentEditionLanguage.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (x) => (this.typedTagsTranslations = x) });

    this.typedTags.valueChanges.subscribe(() => {
      this.populateTagLists();
    });

    this.populateTagLists();
  }

  populateTagLists() {
    this.existingTopicTags = [];
    this.existingTags = [];
    this.existingCompanyTags = [];
    this.existingManagerTags = [];
    this.existingFundTags = [];
    this.existingSectorTags = [];
    this.existingAssetClassTags = [];
    this.existingInvestmentTrustTags = [];
    this.existingShareTags = [];

    if (this.typedTags.value.length) {
      const tagBuckets: Record<SurtitleTagType, ISelectListItem[]> = {
        [SurtitleTagType.Topic]: this.existingTopicTags,
        [SurtitleTagType.Soft]: this.existingTags,
        [SurtitleTagType.Company]: this.existingCompanyTags,
        [SurtitleTagType.Manager]: this.existingManagerTags,
        [SurtitleTagType.Fund]: this.existingFundTags,
        [SurtitleTagType.Sector]: this.existingSectorTags,
        [SurtitleTagType.AssetClass]: this.existingAssetClassTags,
        [SurtitleTagType.InvestmentTrust]: this.existingInvestmentTrustTags,
        [SurtitleTagType.Share]: this.existingShareTags,
        [SurtitleTagType.Person]: [],
        [SurtitleTagType.None]: [],
      };

      for (const { tagType, tag } of this.typedTags.value) {
        const bucket = tagBuckets[tagType];
        if (bucket) {
          bucket.push(tag);
        }
      }
    }
  }

  onSelectExistingTag(tag: ISelectListItem): void {
    let displayName: string = tag.displayName;
    const matchingTag = this.typedTags.controls.find(
      (typedTag) => typedTag.value.tag.id === tag.id
    );

    this.surtitleControl.clear();
    this.surtitleControl.push(
      new FormControl(
        new Surtitle(
          tag.id,
          tag.displayName,
          displayName,
          matchingTag.value.tagType
        )
      )
    );
  }

  createSoftTag(tagName: string) {
    this.dialogService
      .openConfirmationDialog({
        title: 'Add surtitle as soft tag?',
        message: `Are you sure you want to add the new surtitle "${tagName}" to the soft tag pool?`,
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this.taggingService.create$(tagName).pipe(
            catchError((x) => {
              this.dialogService.openErrorDialog({
                message: x,
              });
              return of(null);
            })
          );
        })
      )
      .subscribe({
        next: (newTag: ICreateTagResponse) => {
          this.addSoftTagAsSurtitle(
            new AutoCompleteData(newTag.tagId, tagName)
          );

          this.softTagSearchResults = [];
          this.searchSoftTagFormControl.setValue('');
        },
      });
  }

  removeSurtitle() {
    this.surtitleControl.clear();
    this.selectedSurtitleList = [];
  }

  addSoftTagAsSurtitle($event: IAutoCompleteData) {
    if (
      !this.surtitleControl.value.some(
        (surtitle: Surtitle) => surtitle.id === $event.id
      )
    ) {
      let selectedSoftTagForSurtitle = new Surtitle(
        $event.id,
        $event.displayName,
        $event.displayName,
        SurtitleTagType.Soft
      );

      const typedTagToAdd = new TypedSelectListItem(
        new SelectListItem(
          selectedSoftTagForSurtitle.id,
          selectedSoftTagForSurtitle.name
        ),
        SurtitleTagType.Soft
      );
      this.typedTags.push(new FormControl(typedTagToAdd));

      this.surtitleControl.clear();
      this.surtitleControl.push(new FormControl(selectedSoftTagForSurtitle));

      if (
        !this.tagControl.value.some(
          (x) => x.id === selectedSoftTagForSurtitle.id
        )
      ) {
        this.tagControl.push(
          new FormControl(
            new Tag(
              selectedSoftTagForSurtitle.id,
              TagType.Soft,
              selectedSoftTagForSurtitle.name
            )
          )
        );
      }
    }

    this.softTagSearchResults = [];
  }

  applyDisplayText(displayName: string) {
    const updatedValue = [...this.surtitleControl.value];
    updatedValue[0] = { ...updatedValue[0], displayText: displayName };
    this.surtitleControl.setValue(updatedValue);
  }

  getSurtitleTypeString(type: SurtitleTypeString): string {
    if (
      typeof type === 'string' &&
      Object.values(SurtitleTypeString).includes(type)
    ) {
      let tagTypeString = type.replace(/([a-z])([A-Z])/g, '$1 $2');
      if (type === SurtitleTypeString.Soft) {
        tagTypeString = tagTypeString + ' tag';
      }
      return tagTypeString;
    }
    return 'Unknown';
  }

  checkIfTranslationAvailable() {
    let translation: string;
    const matchingTag = this.typedTags.controls.find((typedTag) => {
      return (
        typedTag.value.tag.id === this.surtitleControl.controls[0].value.id
      );
    });

    if (matchingTag && matchingTag.value.tagType === SurtitleTagType.Topic) {
      const detailTag = this.detailedTopicList.value.find(
        (x) => x.id === Number(this.surtitleControl.controls[0].value.id)
      );
      if (detailTag) {
        translation = detailTag.displayNames?.find(
          (translations) =>
            translations.language === this.currentEditionLanguage.value
        ).displayName;
      }
    }

    if (this.currentEditionLanguage.value !== 'en') {
      if (
        matchingTag &&
        matchingTag.value.tagType === SurtitleTagType.AssetClass
      ) {
        translation =
          this.typedTagsTranslations.assetClassNameResources[
            this.surtitleControl.controls[0].value.id
          ];
      }

      if (matchingTag && matchingTag.value.tagType === SurtitleTagType.Sector) {
        translation =
          this.typedTagsTranslations.sectorNameResources[
            this.surtitleControl.controls[0].value.id
          ];
      }
    }

    if (
      translation &&
      this.surtitleControl.controls[0].value.displayText !== translation
    ) {
      this.surtitleControl.controls[0].patchValue(
        {
          ...this.surtitleControl.controls[0].value,
          displayText: translation,
        },
        { emitEvent: false }
      );
    } else if (
      !translation &&
      this.surtitleControl.controls[0].value.surtitleType === 'Topic'
    ) {
      this.surtitleControl.controls[0].value.displayText =
        this.surtitleControl.controls[0].value.name;
    }

    this.surtitleDisplayNameInput.setValue(
      this.surtitleControl.value[0]?.displayText?.trim()
        ? this.surtitleControl.value[0].displayText
        : this.surtitleControl.value[0]?.name || '',
      { emitEvent: false }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
